<template>
  <div class="checkout-page min-h-screen flex flex-col items-center pt-24">
    <!-- ===== RECEIPT (after order) ===== -->
    <div
      v-if="orderPlaced"
      class="flex flex-col items-center justify-center w-full h-full min-h-[60vh] p-6"
    >
      <div
        class="receipt-print panel rounded-lg p-8 shadow-lg max-w-2xl w-full"
      >
        <div class="text-center mb-6">
          <h2 class="text-3xl font-bold accent mb-2">
            {{ $t("checkout.title") }}
          </h2>
          <p class="muted">
            {{ $t("checkout.orderNumber") }}{{ generateOrderNumber() }}
          </p>
          <p class="muted">
            {{ new Date().toLocaleDateString() }}
            {{ $t("checkout.dateAt") }}
            {{ new Date().toLocaleTimeString() }}
          </p>
        </div>

        <!-- Receipt Header -->
        <div class="border-b pb-4 mb-6" style="border-color: var(--primary)">
          <div class="flex justify-between items-center">
            <div>
              <h3 class="text-xl font-bold accent">
                {{ $t("checkout.restaurantName") }}
              </h3>
              <p class="muted">{{ $t("checkout.restaurantDesc") }}</p>
            </div>
            <div class="text-right">
              <p class="text-sm muted">{{ $t("checkout.orderStatus") }}</p>
              <p class="font-semibold" style="color: var(--accent)">
                {{ $t("checkout.confirmed") }}
              </p>
            </div>
          </div>
        </div>

        <!-- Order Items -->
        <div class="mb-6">
          <h4 class="text-lg font-semibold accent mb-4">
            {{ $t("checkout.orderDetails") }}
          </h4>
          <div class="space-y-3">
            <div
              v-for="(item, index) in orderData.cart"
              :key="index"
              class="flex justify-between items-center py-2 border-b"
              style="border-color: var(--border)"
            >
              <div class="flex-1">
                <p class="font-semibold">
                  {{ getTranslatedDishName(item.name, t) }}
                </p>
                <p class="text-sm muted">
                  {{ $t("checkout.quantity") }} {{ item.quantity || 1 }}
                </p>
                <p
                  v-if="item.specialRequest && item.specialRequest !== 'none'"
                  class="text-sm accent mt-1"
                >
                  {{ $t("checkout.specialRequest") }}: {{ item.specialRequest }}
                </p>
              </div>
              <div class="text-right">
                <p class="font-semibold">
                  {{
                    $t("checkout.currency", {
                      amount: (item.price * (item.quantity || 1)).toFixed(2),
                    })
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Delivery Information -->
        <div v-if="deliveryAddress" class="mb-6 p-4 subpanel rounded">
          <h4 class="text-lg font-semibold accent mb-2">
            {{ $t("checkout.deliveryAddress") }}
          </h4>
          <p>{{ deliveryAddress }}</p>
        </div>

        <!-- Special Request -->
        <div v-if="specialRequest" class="mb-6 p-4 subpanel rounded">
          <h4 class="text-lg font-semibold accent mb-2">
            {{ $t("checkout.specialRequest") }}
          </h4>
          <p>{{ specialRequest }}</p>
        </div>

        <!-- Payment Summary -->
        <div class="border-t pt-4" style="border-color: var(--primary)">
          <div class="space-y-2">
            <div class="flex justify-between">
              <span class="muted">{{ $t("checkout.subtotal") }}</span>
              <span>{{
                $t("checkout.currency", {
                  amount: orderData.cartTotal.toFixed(2),
                })
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="muted">{{ $t("checkout.serviceFee") }}</span>
              <span>{{
                $t("checkout.currency", {
                  amount: orderData.serviceFee.toFixed(2),
                })
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="muted">{{ $t("checkout.deliveryFee") }}</span>
              <span>{{
                $t("checkout.currency", {
                  amount: orderData.deliveryFee.toFixed(2),
                })
              }}</span>
            </div>
            <div
              class="flex justify-between text-lg font-bold border-t pt-2"
              style="border-color: var(--border)"
            >
              <span class="accent">{{ $t("checkout.total") }}</span>
              <span class="accent">
                {{
                  $t("checkout.currency", {
                    amount: orderData.totalAmount.toFixed(2),
                  })
                }}
              </span>
            </div>
          </div>
        </div>

        <!-- Payment Method -->
        <div class="mt-6 p-4 subpanel rounded">
          <h4 class="text-lg font-semibold accent mb-2">
            {{ $t("checkout.paymentMethod") }}
          </h4>
          <p class="muted">
            {{
              paymentMethod === "cash"
                ? $t("checkout.cashOnDelivery")
                : $t("checkout.creditDebitCard")
            }}
          </p>
          <p
            v-if="paymentMethod === 'card'"
            class="text-sm"
            style="color: var(--accent)"
          >
            {{ $t("checkout.paymentSuccessfulReceipt") }}
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="mt-8 flex gap-4">
          <button @click="printReceipt" class="flex-1 btn-primary">
            {{ $t("checkout.printReceipt") }}
          </button>
          <button @click="goToHome" class="flex-1 btn-ghost">
            {{ $t("checkout.backToHome") }}
          </button>
        </div>
      </div>
    </div>

    <!-- ===== MAIN CHECKOUT (before order) ===== -->
    <template v-else>
      <!-- Order Summary -->
      <section class="panel w-4/5 rounded-lg p-8 shadow-lg my-12">
        <h2 class="text-2xl font-bold accent mb-6">
          {{ $t("checkout.orderSummary") }}
        </h2>

        <ul
          v-if="cart.length"
          class="divide-y"
          style="--divide-color: var(--border)"
        >
          <li
            v-for="(item, idx) in cart"
            :key="idx"
            class="py-2 flex justify-between items-center"
          >
            <div class="flex items-center gap-3">
              <div class="flex items-center gap-2">
                <button @click="decreaseQuantity(idx)" class="circle-btn">
                  -
                </button>
                <span class="circle-qty">{{ item.quantity || 1 }}</span>
                <button @click="increaseQuantity(idx)" class="circle-btn">
                  +
                </button>
              </div>
              <div class="flex flex-col">
                <span class="ml-2">{{
                  getTranslatedDishName(item.name, t)
                }}</span>
                <span
                  v-if="item.specialRequest && item.specialRequest !== 'none'"
                  class="ml-2 text-sm accent"
                >
                  {{ $t("checkout.specialRequest") }}: {{ item.specialRequest }}
                </span>
              </div>
            </div>
            <span>{{
              $t("checkout.currency", {
                amount: (item.price * (item.quantity || 1)).toFixed(2),
              })
            }}</span>
          </li>
        </ul>

        <div v-else class="muted mb-4">{{ $t("cart.empty") }}</div>

        <div class="space-y-2">
          <div class="flex justify-between">
            <span>{{ $t("checkout.subtotal") }}</span>
            <span>{{
              $t("checkout.currency", { amount: cartTotal.toFixed(2) })
            }}</span>
          </div>
          <div class="flex justify-between">
            <span>{{ $t("checkout.serviceFee") }}</span>
            <span>{{
              $t("checkout.currency", { amount: serviceFee.toFixed(2) })
            }}</span>
          </div>
          <div
            class="flex justify-between font-semibold text-lg border-t pt-2"
            style="border-color: var(--border)"
          >
            <span>{{ $t("checkout.total") }}</span>
            <span>{{
              $t("checkout.currency", {
                amount: (cartTotal + serviceFee).toFixed(2),
              })
            }}</span>
          </div>
        </div>

        <button @click="goToMenu" class="mt-6 w-full btn-primary">
          {{ $t("checkout.modify") }}
        </button>
      </section>

      <!-- In-house pre-order -->
      <div
        class="mt-4 w-4/5 flex justify-center md:justify-end items-center gap-3"
      >
        <button
          @click="goToDeposit"
          class="font-['Cormorant_SC'] text-lg btn-ghost glow-gold"
        >
          {{ $t("checkout.inHousePreOrder") }}
        </button>

        <!-- Information Icon + Tooltip -->
        <div class="relative">
          <div
            @mouseenter="showInfoTooltip = true"
            @mouseleave="showInfoTooltip = false"
            class="cursor-help accent"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <div
            v-if="showInfoTooltip"
            class="absolute bottom-full right-0 mb-2 px-3 py-2 tooltip panel border border-gold z-10"
            style="max-width: 220px; min-width: 160px; word-break: break-word"
            :class="{
              'left-1/2 transform -translate-x-1/2': $i18n.locale !== 'ar',
              'right-1/2 transform translate-x-1/2': $i18n.locale === 'ar',
            }"
          >
            <div>{{ $t("checkout.inHousePreOrderHover") }}</div>
            <div
              class="absolute top-full w-0 h-0"
              style="
                border-left: 4px solid transparent;
                border-right: 4px solid transparent;
                border-top: 4px solid var(--surface);
              "
              :class="{
                'left-1/2 transform -translate-x-1/2': $i18n.locale !== 'ar',
                'right-1/2 transform translate-x-1/2': $i18n.locale === 'ar',
              }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Delivery Method -->
      <section class="panel w-4/5 rounded-lg p-8 shadow-lg my-12">
        <h2 class="text-2xl font-bold accent mb-6">
          {{ $t("checkout.deliveryMethod") }}
        </h2>
        <label for="deliveryAddress" class="block text-lg mb-2">{{
          $t("checkout.deliveryAddressLabel")
        }}</label>
        <input
          id="deliveryAddress"
          :value="showAddressModal ? liveAddress : deliveryAddress"
          type="text"
          class="input-solid w-full mb-4"
          readonly
        />
        <div class="flex gap-4">
          <button @click="showMap = true" class="flex-1 btn-primary">
            {{ $t("checkout.selectOnMap") }}
          </button>
          <button @click="openAddressModal" class="flex-1 btn-ghost">
            {{ $t("checkout.addAddress") }}
          </button>
        </div>
      </section>

      <!-- Add Address Modal -->
      <div
        v-if="showAddressModal"
        class="fixed inset-0 z-50 flex items-center justify-center"
        style="background: rgba(0, 0, 0, 0.7)"
      >
        <div
          class="panel p-6 rounded-lg w-full max-w-md flex flex-col gap-4 relative overflow-y-auto max-h-[90vh]"
        >
          <h3 class="text-xl font-bold accent mb-4 text-center">
            {{ $t("checkout.addAddressDetails") }}
          </h3>
          <div class="flex flex-col gap-4 w-full">
            <label class="accent font-semibold w-full">
              {{ $t("checkout.city") }}
              <input
                v-model="addressForm.city"
                type="text"
                class="input-solid mt-1"
                :placeholder="$t('checkout.city')"
              />
            </label>
            <label class="accent font-semibold w-full">
              {{ $t("checkout.block") }}
              <input
                v-model="addressForm.block"
                type="text"
                class="input-solid mt-1"
                :placeholder="$t('checkout.block')"
              />
            </label>
            <label class="accent font-semibold w-full">
              {{ $t("checkout.streetName") }}
              <input
                v-model="addressForm.street"
                type="text"
                class="input-solid mt-1"
                :placeholder="$t('checkout.streetName')"
              />
            </label>
            <label class="accent font-semibold w-full">
              {{ $t("checkout.buildingName") }}
              <input
                v-model="addressForm.building"
                type="text"
                class="input-solid mt-1"
                :placeholder="$t('checkout.buildingName')"
              />
            </label>
            <label class="accent font-semibold w-full">
              {{ $t("checkout.floor") }}
              <input
                v-model="addressForm.floor"
                type="text"
                class="input-solid mt-1"
                :placeholder="$t('checkout.floor')"
              />
            </label>
            <label class="accent font-semibold w-full">
              {{ $t("checkout.additionalDetails") }}
              <input
                v-model="addressForm.details"
                type="text"
                class="input-solid mt-1"
                :placeholder="$t('checkout.additionalDetails')"
              />
            </label>
          </div>

          <div
            class="mt-6 text-base font-semibold subpanel rounded p-3 border border-gold w-full"
          >
            <span class="accent">{{ $t("checkout.preview") }} </span
            >{{ liveAddress || $t("checkout.addressPreview") }}
          </div>

          <div class="flex flex-col gap-3 mt-4 w-full">
            <button
              @click="saveAddress"
              :disabled="!isAddressComplete"
              class="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ $t("checkout.save") }}
            </button>
            <button @click="cancelAddress" class="btn-ghost">
              {{ $t("checkout.cancel") }}
            </button>
          </div>
        </div>
      </div>

      <!-- Leaflet Map Popup Modal -->
      <div
        v-show="showMap"
        class="fixed inset-0 z-50 flex items-center justify-center"
        style="background: rgba(0, 0, 0, 0.7)"
      >
        <div
          class="panel p-6 rounded-lg w-full max-w-xl max-h-[90vh] flex flex-col items-center relative"
        >
          <button
            @click="showMap = false"
            class="absolute top-4 right-4 z-50 btn-primary px-4 py-2 text-base"
          >
            {{ $t("checkout.done") }}
          </button>
          <div
            id="leaflet-map"
            class="map-popup w-full h-80 border rounded"
            style="border-color: var(--primary)"
          ></div>
        </div>
      </div>

      <!-- Special Request -->
      <section class="panel w-4/5 rounded-lg p-8 shadow-lg my-12">
        <h2 class="text-2xl font-bold accent mb-6">
          {{ $t("checkout.specialRequest") }}
        </h2>
        <label for="specialRequest" class="block text-lg mb-2">{{
          $t("checkout.specialRequestLabel")
        }}</label>

        <div v-if="specialRequestSubmitted" class="flex flex-col gap-3">
          <div class="w-full rounded input-solid min-h-[3rem]">
            <span v-if="specialRequest">{{ specialRequest }}</span>
            <span v-else class="muted">{{
              $t("checkout.noSpecialRequest")
            }}</span>
          </div>
          <button @click="editSpecialRequest" class="w-full btn-primary">
            {{ $t("checkout.edit") }}
          </button>
        </div>

        <div v-else>
          <textarea
            id="specialRequest"
            v-model="specialRequest"
            rows="4"
            class="w-full input-solid resize-none focus:outline-none"
            :placeholder="$t('checkout.specialRequestPlaceholder')"
          ></textarea>
        </div>
      </section>

      <!-- Payment Summary -->
      <section class="panel w-4/5 rounded-lg p-8 shadow-lg my-12">
        <h2 class="text-2xl font-bold accent mb-6">
          {{ $t("checkout.paymentSummary") }}
        </h2>

        <div class="flex flex-col gap-4 mb-6">
          <!-- Cash -->
          <label class="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              value="cash"
              v-model="paymentMethod"
              class="radio-accent"
            />
            <span class="flex items-center gap-2">
              <!-- put the SVG back -->
              <svg class="pay-icon" viewBox="0 0 24 24" aria-hidden="true">
                <rect
                  x="2"
                  y="7"
                  width="20"
                  height="10"
                  rx="2"
                  style="fill: var(--primary)"
                />
                <circle cx="7" cy="12" r="1.5" style="fill: var(--surface)" />
                <circle cx="17" cy="12" r="1.5" style="fill: var(--surface)" />
              </svg>
              {{ $t("checkout.cashOnDelivery") }}
            </span>
          </label>

          <!-- Card -->
          <label class="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              value="card"
              v-model="paymentMethod"
              class="radio-accent"
            />
            <span class="flex items-center gap-2">
              <!-- put the SVG back -->
              <svg class="pay-icon" viewBox="0 0 24 24" aria-hidden="true">
                <rect
                  x="2"
                  y="6"
                  width="20"
                  height="12"
                  rx="2"
                  style="fill: var(--primary)"
                />
                <rect
                  x="2"
                  y="10"
                  width="20"
                  height="2"
                  style="fill: var(--bg)"
                />
                <rect
                  x="6"
                  y="14"
                  width="4"
                  height="2"
                  rx="1"
                  style="fill: var(--bg)"
                />
              </svg>
              {{ $t("checkout.addNewCard") }}
            </span>
          </label>
        </div>

        <!-- Cash summary -->
        <div v-if="paymentMethod === 'cash'" class="mb-6">
          <div class="flex justify-between py-2">
            <span class="text-lg">{{ $t("checkout.subtotal") }}</span>
            <span class="text-lg">{{
              $t("checkout.currency", { amount: cartTotal.toFixed(2) })
            }}</span>
          </div>
          <div class="flex justify-between py-2">
            <span class="text-lg">{{ $t("checkout.serviceFee") }}</span>
            <span class="text-lg">{{
              $t("checkout.currency", { amount: serviceFee.toFixed(2) })
            }}</span>
          </div>
          <div class="flex justify-between py-2">
            <span class="text-lg">{{ $t("checkout.deliveryFee") }}</span>
            <span class="text-lg">{{
              $t("checkout.currency", { amount: "50.00" })
            }}</span>
          </div>
          <div
            class="flex justify-between py-2 font-bold text-xl border-t mt-2 pt-2"
            style="border-color: var(--primary)"
          >
            <span>{{ $t("checkout.total") }}</span>
            <span>{{
              $t("checkout.currency", {
                amount: (cartTotal + serviceFee + 50).toFixed(2),
              })
            }}</span>
          </div>
        </div>

        <!-- Card summary + Stripe -->
        <div v-if="paymentMethod === 'card'" class="mb-6">
          <div class="mb-6">
            <div class="flex justify-between py-2">
              <span class="text-lg">{{ $t("checkout.subtotal") }}</span>
              <span class="text-lg">{{
                $t("checkout.currency", { amount: cartTotal.toFixed(2) })
              }}</span>
            </div>
            <div class="flex justify-between py-2">
              <span class="text-lg">{{ $t("checkout.serviceFee") }}</span>
              <span class="text-lg">{{
                $t("checkout.currency", { amount: serviceFee.toFixed(2) })
              }}</span>
            </div>
            <div class="flex justify-between py-2">
              <span class="text-lg">{{ $t("checkout.deliveryFee") }}</span>
              <span class="text-lg">{{
                $t("checkout.currency", { amount: "50.00" })
              }}</span>
            </div>
            <div
              class="flex justify-between py-2 font-bold text-xl border-t mt-2 pt-2"
              style="border-color: var(--primary)"
            >
              <span>{{ $t("checkout.total") }}</span>
              <span>{{
                $t("checkout.currency", {
                  amount: (cartTotal + serviceFee + 50).toFixed(2),
                })
              }}</span>
            </div>
          </div>

          <div class="space-y-4">
            <div class="space-y-2">
              <span class="font-semibold text-lg muted"
                >PAYMENT INFORMATION</span
              >
              <div class="w-full h-px" style="background: var(--primary)"></div>
            </div>

            <div v-if="!stripeLoaded" class="text-center py-8">
              <div class="spinner mx-auto"></div>
              <p class="muted mt-2">{{ $t("checkout.loadingPayment") }}</p>
            </div>

            <div v-else>
              <div
                id="card-element"
                class="w-full px-4 py-3 rounded input-solid min-h-[50px]"
              ></div>
              <div v-if="cardError" class="text-sm" style="color: #fa755a">
                {{ cardError }}
              </div>

              <div
                v-if="paymentStatus"
                class="mt-4 p-3 rounded"
                :class="
                  paymentStatus === 'processing'
                    ? 'status-processing'
                    : paymentStatus === 'success'
                    ? 'status-success'
                    : 'status-failed'
                "
              >
                <div
                  v-if="paymentStatus === 'processing'"
                  class="flex items-center"
                >
                  <div class="spinner sm mr-2"></div>
                  {{ $t("checkout.processingPayment") }}
                </div>
                <div
                  v-else-if="paymentStatus === 'success'"
                  class="flex items-center"
                >
                  <span class="status-dot mr-2"></span>
                  {{ $t("checkout.paymentSuccessful") }}
                </div>
                <div v-else class="flex items-center">
                  <span class="status-dot mr-2"></span>
                  {{ $t("checkout.paymentFailed") }}
                </div>
              </div>
            </div>

            <div
              class="flex items-center  p-3 border rounded"
              style="border-color: var(--primary)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 24 24"
                style="color: var(--primary)"
              >
                <path
                  d="M12 1a5 5 0 00-5 5v4H6a2 2 0 00-2 2v10a2 
         2 0 002 2h12a2 2 0 002-2V12a2 2 0 
         00-2-2h-1V6a5 5 0 00-5-5zm-3 9V6a3 3 0 
         116 0v4H9z"
                />
              </svg>

              <p class="text-xs muted">
                {{ $t("checkout.securityNote") }}
              </p>
            </div>
          </div>
        </div>

        <button
          :disabled="
            !paymentMethod ||
            (paymentMethod === 'card' && cardError) ||
            !deliveryAddress ||
            deliveryAddress.trim() === '' ||
            cart.length === 0
          "
          @click="placeOrder"
          class="mt-6 w-full btn-primary text-lg disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {{ $t("checkout.placeOrder") }}
        </button>
      </section>
    </template>
  </div>
</template>

<script>
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { getStripe, createPaymentIntent } from "@/stripe.js";
import { getTranslatedDishName } from "@/utils/dishTranslation.js";
import { useI18n } from "vue-i18n";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
  updateDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { sendCheckoutEmail } from "@/utils/sendCheckoutEmail.js";

export default {
  name: "CheckoutPage",
  data() {
    return {
      cart: [],
      showMap: false,
      showAddressModal: false,
      deliveryAddress: "",
      specialRequest: "",
      specialRequestSubmitted: false,
      leafletMap: null,
      leafletMarker: null,
      addressForm: {
        city: "",
        block: "",
        street: "",
        building: "",
        floor: "",
        details: "",
      },
      lastSavedAddress: "",
      paymentMethod: "",
      cardForm: {
        number: "",
        expiry: "",
        cvv: "",
      },
      orderPlaced: false,
      // Order data for receipt
      orderData: {
        cart: [],
        cartTotal: 0,
        serviceFee: 0,
        deliveryFee: 50.0,
        totalAmount: 0,
      },
      // Stripe related data
      stripe: null,
      elements: null,
      cardElement: null,
      stripeLoaded: false,
      cardError: null,
      paymentStatus: null,
      clientSecret: null,
      showInfoTooltip: false,
    };
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  computed: {
    cartTotal() {
      return this.cart.reduce(
        (sum, item) => sum + item.price * (item.quantity || 1),
        0
      );
    },
    serviceFee() {
      return this.cartTotal * 0.15; // 15% service fee
    },
    liveAddress() {
      const { city, block, street, building, floor, details } =
        this.addressForm;
      return [city, block, street, building, floor, details]
        .filter(Boolean)
        .join(", ");
    },
    isAddressComplete() {
      const { city, block, street, building } = this.addressForm;
      return city && block && street && building;
    },
  },
  watch: {
    showMap(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.initLeafletMap();

            if (this.leafletMap) {
              this.leafletMap.invalidateSize(); // Force redraw
            }
          }, 500); // Give modal time to animate in
        });
      }
    },
    paymentMethod(newVal) {
      // Clear previous errors and status when switching payment methods
      this.cardError = null;
      this.paymentStatus = null;

      if (newVal === "card") {
        // Initialize Stripe Elements when card payment is selected
        this.$nextTick(() => {
          if (this.stripeLoaded) {
            this.setupStripeElements();
          }
        });
      }
    },
  },

  async mounted() {
    const storedCart = localStorage.getItem("cart");
    this.cart = storedCart ? JSON.parse(storedCart) : [];

    // Redirect to menu if cart is empty
    if (!this.cart || this.cart.length === 0) {
      this.$router.push({ name: "Menu" });
      return;
    }

    // Load saved delivery address from user profile
    await this.loadSavedDeliveryAddress();

    // Initialize Stripe
    await this.initializeStripe();
  },
  methods: {
    goToMenu() {
      this.$router.push({ name: "Menu" });
    },
    goToDeposit() {
      this.$router.push({ name: "bookingduration" });
    },
    increaseQuantity(idx) {
      this.cart[idx].quantity = (this.cart[idx].quantity || 1) + 1;
      localStorage.setItem("cart", JSON.stringify(this.cart));
    },
    decreaseQuantity(idx) {
      if ((this.cart[idx].quantity || 1) > 1) {
        this.cart[idx].quantity -= 1;
      } else {
        this.cart.splice(idx, 1);
      }
      localStorage.setItem("cart", JSON.stringify(this.cart));
    },
    editSpecialRequest() {
      this.specialRequestSubmitted = false;
    },
    openAddressModal() {
      this.lastSavedAddress = this.deliveryAddress;
      // If editing, parse the address back into fields (optional, not implemented here)
      this.showAddressModal = true;
    },
    saveAddress() {
      this.deliveryAddress = this.liveAddress;
      this.showAddressModal = false;
    },
    cancelAddress() {
      // Reset form to last saved address (optional: clear fields)
      this.showAddressModal = false;
      // Optionally reset fields:
      // this.addressForm = { city: '', block: '', street: '', building: '', floor: '', details: '' };
    },
    initLeafletMap() {
      const container = document.getElementById("leaflet-map");
      if (!container) return;

      if (this.leafletMap) {
        this.leafletMap.remove();
        this.leafletMap = null;
        this.leafletMarker = null;
      }

      this.leafletMap = L.map("leaflet-map", {
        zoomControl: true,
        scrollWheelZoom: true,
      }).setView([51.505, -0.09], 13);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "© OpenStreetMap contributors",
      }).addTo(this.leafletMap);

      this.leafletMarker = L.marker([51.505, -0.09]).addTo(this.leafletMap);
      this.deliveryAddress = "51.505, -0.09";

      this.leafletMap.on("click", this.onMapClick);
      this.leafletMap.invalidateSize();
      this.getUserLocation();
    },
    onMapClick(e) {
      const { lat, lng } = e.latlng;

      if (this.leafletMarker) {
        this.leafletMarker.setLatLng([lat, lng]);
      } else {
        this.leafletMarker = L.marker([lat, lng]).addTo(this.leafletMap);
      }

      this.deliveryAddress = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      this.reverseGeocode(lat, lng);
    },
    getUserLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            this.leafletMap.setView([latitude, longitude], 15);
            this.leafletMarker.setLatLng([latitude, longitude]);
            this.deliveryAddress = `${latitude.toFixed(6)}, ${longitude.toFixed(
              6
            )}`;
            this.reverseGeocode(latitude, longitude);
          },
          (error) => {
            console.log("Geolocation error:", error);
          }
        );
      }
    },
    async reverseGeocode(lat, lng) {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
        );
        const data = await response.json();

        if (data.display_name) {
          this.deliveryAddress = `${data.display_name} (${lat.toFixed(
            6
          )}, ${lng.toFixed(6)})`;
        }
      } catch (error) {
        console.error("Reverse geocoding failed:", error);
      }
    },
    saveCard() {
      // this.showCardModal = false; // This line is removed as per the edit hint
    },
    cancelCard() {
      // this.showCardModal = false; // This line is removed as per the edit hint
      // Optionally clear card form fields:
      // this.cardForm = { number: '', expiry: '', cvv: '' };
    },
    placeOrder() {
      // Check if cart is empty
      if (!this.cart || this.cart.length === 0) {
        alert(
          this.$t("checkout.cartEmpty") ||
            "Your cart is empty. Please add items before placing an order."
        );
        this.$router.push({ name: "Menu" });
        return;
      }

      // Check if address is provided
      if (!this.deliveryAddress || this.deliveryAddress.trim() === "") {
        alert(
          this.$t("checkout.addressRequired") ||
            "Please add a delivery address before placing your order."
        );
        return;
      }

      if (this.paymentMethod === "cash") {
        // Create order document for cash payment
        this.createOrderDocument()
          .then(() => {
            this.orderPlaced = true;
            this.scrollToReceipt();
          })
          .catch((error) => {
            console.error("Failed to create order:", error);
            alert("Failed to place order. Please try again.");
          });
      } else if (this.paymentMethod === "card") {
        this.processStripePayment();
      }
    },

    // Stripe Methods
    async initializeStripe() {
      try {
        this.stripe = await getStripe();
        if (!this.stripe) {
          console.error("Failed to load Stripe");
          return;
        }

        this.stripeLoaded = true;
        // Don't automatically set up elements - wait for user to select card payment
      } catch (error) {
        console.error("Error initializing Stripe:", error);
      }
    },

    setupStripeElements() {
      if (!this.stripe) return;

      // Always destroy existing card element before creating a new one
      if (this.cardElement) {
        this.cardElement.destroy();
        this.cardElement = null;
      }

      // read theme tokens from :root (works for both dark and [data-theme="light"])
      const css = getComputedStyle(document.documentElement);
      const c = (v) => css.getPropertyValue(v).trim();

      const textColor = c("--text") || "#111111";
      const placeholderColor = c("--muted") || "#5a5a5a";
      const bgColor = c("--surface-2") || "#ffffff";
      const invalidColor = c("--danger") || "#f87171";

      this.elements = this.stripe.elements();

      // Create card element
      this.cardElement = this.elements.create("card", {
        style: {
          base: {
            color: textColor,
            fontSize: "16px",
            fontFamily: "Arial, sans-serif",
            "::placeholder": {
              color: placeholderColor,
            },
            backgroundColor: bgColor,
          },
          invalid: {
            color: invalidColor,
            iconColor: invalidColor,
          },
        },
      });

      // Wait for DOM to be ready before mounting
      this.$nextTick(() => {
        const cardElementContainer = document.getElementById("card-element");
        if (cardElementContainer) {
          this.cardElement.mount("#card-element");

          // Handle card validation errors
          this.cardElement.on("change", (event) => {
            this.cardError = event.error ? event.error.message : null;
          });
        } else {
          console.error("Card element container not found");
        }
      });
    },

    async processStripePayment() {
      if (!this.stripe || !this.cardElement) {
        this.cardError = "Payment system not ready. Please try again.";
        return;
      }

      // Check if there are any card validation errors
      if (this.cardError) {
        this.paymentStatus = "failed";
        return;
      }

      // Validate card completeness
      const cardElement = this.cardElement;
      const { error } = await this.stripe.createToken(cardElement);

      if (error) {
        this.cardError = error.message;
        this.paymentStatus = "failed";
        return;
      }

      this.paymentStatus = "processing";
      this.cardError = null;

      try {
        // Create payment intent
        const totalAmount = this.cartTotal + 50; // Including delivery fee
        this.clientSecret = await createPaymentIntent(totalAmount);

        if (!this.clientSecret) {
          throw new Error("Failed to create payment intent");
        }

        // Check if this is a mock payment (for testing)
        if (this.clientSecret.startsWith("pi_mock_")) {
          // For mock payments, only proceed if there are no validation errors
          if (!this.cardError) {
            // Simulate successful payment for testing
            this.paymentStatus = "success";
            setTimeout(async () => {
              try {
                await this.createOrderDocument();
                this.orderPlaced = true;
                this.scrollToReceipt();
              } catch (error) {
                console.error("Failed to create order:", error);
                alert(
                  "Payment successful but failed to save order. Please contact support."
                );
              }
            }, 2000);
          } else {
            // If there are validation errors, don't process payment
            this.paymentStatus = "failed";
          }
          return;
        }

        // Confirm payment with Stripe
        const { error, paymentIntent } = await this.stripe.confirmCardPayment(
          this.clientSecret,
          {
            payment_method: {
              card: this.cardElement,
              billing_details: {
                // You can add customer details here
              },
            },
          }
        );

        if (error) {
          this.cardError = error.message;
          this.paymentStatus = "failed";
        } else if (paymentIntent.status === "succeeded") {
          this.paymentStatus = "success";
          // Wait a moment to show success message
          setTimeout(async () => {
            try {
              await this.createOrderDocument();
              this.orderPlaced = true;
              this.scrollToReceipt();
            } catch (error) {
              console.error("Failed to create order:", error);
              alert(
                "Payment successful but failed to save order. Please contact support."
              );
            }
          }, 2000);
        }
      } catch (error) {
        console.error("Payment error:", error);
        this.cardError = "Payment failed. Please try again.";
        this.paymentStatus = "failed";
      }
    },

    // Cleanup Stripe elements
    cleanupStripe() {
      if (this.cardElement) {
        this.cardElement.destroy();
      }
    },

    // Generate unique order number
    generateOrderNumber() {
      const timestamp = Date.now().toString().slice(-6);
      const random = Math.random().toString(36).substr(2, 4).toUpperCase();
      return `TH${timestamp}${random}`;
    },

    // Create order document in Firestore
    async createOrderDocument() {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
          console.error("No authenticated user found");
          return;
        }

        const db = getFirestore();

        // Prepare order items with special requests
        const orderItems = this.cart.map((item) => ({
          dishName: getTranslatedDishName(item.name, this.t),
          count: item.quantity || 1,
          price: item.price,
          specialRequest: item.specialRequest || "none",
        }));

        // Create the order document
        const deliveryOrder = {
          userId: user.uid,
          userEmail: user.email,
          dishes: orderItems,
          deliveryAddress: this.deliveryAddress,
          specialRequest: this.specialRequest || "none",
          paymentMethod: this.paymentMethod,
          subtotal: this.cartTotal,
          serviceFee: this.serviceFee,
          deliveryFee: 50.0,
          totalAmount: this.cartTotal + this.serviceFee + 50,
          status: "pending",
          orderNumber: this.generateOrderNumber(),
          createdAt: serverTimestamp(),
        };

        // Add to Firestore
        const docRef = await addDoc(
          collection(db, "deliveryOrders"),
          deliveryOrder
        );
        console.log("Order document created with ID:", docRef.id);

        // Save delivery address to user profile for future use
        try {
          await updateDoc(doc(db, "users", user.uid), {
            deliveryAddress: this.deliveryAddress,
            updatedAt: new Date(),
          });
          console.log("Delivery address saved to user profile");
        } catch (error) {
          console.error(
            "Error saving delivery address to user profile:",
            error
          );
          // Don't throw error here as order was already created successfully
        }

        // Store order data for receipt before clearing cart
        this.orderData = {
          cart: [...this.cart],
          cartTotal: this.cartTotal,
          serviceFee: this.serviceFee,
          deliveryFee: 50.0,
          totalAmount: this.cartTotal + this.serviceFee + 50,
        };

        // Send confirmation email BEFORE clearing cart
        await sendCheckoutEmail({
          userName: user.displayName || "",
          userEmail: user.email,
          dishes: orderItems,
          deliveryAddress: this.deliveryAddress,
          paymentMethod: this.paymentMethod,
          subtotal: this.cartTotal,
          serviceFee: this.serviceFee,
          totalAmount: this.cartTotal + this.serviceFee + 50,
        });

        // Clear cart after successful order and email sending
        localStorage.removeItem("cart");
        this.cart = [];

        return docRef.id;
      } catch (error) {
        console.error("Error creating order document:", error);
        throw error;
      }
    },

    // Print receipt
    printReceipt() {
      window.print();
    },

    // Go back to home page
    goToHome() {
      this.$router.push({ name: "Home" });
    },
    getTranslatedDishName(dishName, t) {
      return getTranslatedDishName(dishName, t);
    },

    // Save order to history
    saveOrderToHistory() {
      const orderData = {
        orderNumber: this.generateOrderNumber(),
        orderDate:
          new Date().toLocaleDateString() +
          " at " +
          new Date().toLocaleTimeString(),
        orderItems: this.cart.map((item) => ({
          name: item.name,
          price: item.price,
          quantity: item.quantity || 1,
        })),
        paymentMethod: this.paymentMethod,
        deliveryAddress: this.deliveryAddress,
        specialRequest: this.specialRequest,
        total: this.cartTotal + 50,
      };

      // Get existing orders from localStorage
      const existingOrders = JSON.parse(
        localStorage.getItem("orderHistory") || "[]"
      );

      // Add new order to the beginning
      existingOrders.unshift(orderData);

      // Keep only the last 10 orders
      const recentOrders = existingOrders.slice(0, 10);

      // Save back to localStorage
      localStorage.setItem("orderHistory", JSON.stringify(recentOrders));
    },

    // Scroll to receipt when order is placed
    scrollToReceipt() {
      this.$nextTick(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
    },

    // Load saved delivery address from user profile
    async loadSavedDeliveryAddress() {
      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        const db = getFirestore();
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          if (userData.deliveryAddress) {
            this.deliveryAddress = userData.deliveryAddress;
            // Attempt to parse the address back into form fields if possible
            try {
              const addressParts = this.deliveryAddress.split("(");
              const displayAddress = addressParts[0].trim();
              const coordinates = addressParts[1].replace(")", "").trim();
              const [lat, lng] = coordinates.split(",").map(Number);

              this.leafletMap.setView([lat, lng], 15);
              this.leafletMarker.setLatLng([lat, lng]);

              this.addressForm = {
                city: displayAddress.split(",")[0].trim(),
                block: displayAddress.split(",")[1].trim(),
                street: displayAddress.split(",")[2].trim(),
                building: displayAddress.split(",")[3].trim(),
                floor: displayAddress.split(",")[4].trim(),
                details: displayAddress.split(",")[5].trim(),
              };
            } catch (e) {
              console.error("Failed to parse saved address:", e);
            }
          }
        }
      }
    },
  },

  beforeUnmount() {
    if (this.leafletMap) {
      this.leafletMap.remove();
    }
    this.cleanupStripe();
  },
};
</script>

<style scoped>
/* ========= THEME-AWARE HELPERS ========= */
.checkout-page {
  background: var(--bg);
  color: var(--on-surface);
}

/* primary surfaces */
.panel {
  background: var(--bg);
  border: 1px solid var(--border);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  display: block;
}
.subpanel {
  background: var(--surface);
  border: 1px solid var(--border);
}

/* text helpers */
.accent {
  color: var(--primary);
}
.muted {
  color: var(--muted);
}

/* buttons */
.btn-primary {
  background: var(--primary);
  color: var(--on-primary);
  font-weight: 700;
  padding: 0.5rem 1rem;
  border-radius: 0.2rem;
  transition: background 0.15s ease, transform 0.1s ease;
  border: 0;
}
.btn-primary:hover {
  background: var(--primary-200);
  transform: translateY(-1px);
}

.btn-ghost {
  background: transparent;
  color: var(--primary);
  border: 1px solid var(--primary);
  font-weight: 700;
  padding: 0.6rem 1rem;
  border-radius: 0.2rem;
  transition: background 0.15s ease, color 0.15s ease;
}
.btn-ghost:hover {
  background: var(--primary);
  color: var(--on-primary);
}

.glow-gold {
  box-shadow: var(--glow-sm);
}
.glow-gold:hover {
  box-shadow: var(--glow);
}

/* inputs */
.input-solid {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--primary);
  background: var(--surface);
  color: var(--on-surface);
  padding: 0.75rem 1rem;
  border-radius: 0.2rem;
}

.input-solid:focus,
.input-solid:focus-visible {
  outline: none;
  border-color: var(--primary-200);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 35%, transparent);
}

.input-solid::placeholder {
  color: color-mix(in srgb, var(--muted) 85%, transparent);
}

/* radio & icon (accent color) */
.radio-accent {
  width: 20px;
  height: 20px;
  accent-color: var(--primary);
}
.icon-accent {
  width: 20px;
  height: 14px;
  border-radius: 3px;
  display: inline-block;
  background: var(--primary);
}

/* list divider helper (uses --divide-color inline) */
.divide-y > * + * {
  border-top: 1px solid var(--divide-color, var(--border));
}

/* quantity pills */
.circle-btn,
.circle-qty {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1rem;
}
.circle-btn {
  border: 1.5px solid var(--primary);
  background: transparent;
  color: var(--primary);
  transition: background 0.2s;
  cursor: pointer;
}
.circle-btn:hover {
  background: color-mix(in srgb, var(--primary) 12%, transparent);
}
.circle-qty {
  border: 1.5px solid var(--primary);
  color: var(--primary);
  background: transparent;
}

/* tooltip base */
.tooltip {
  color: var(--on-surface);
  border-radius: 8px;
}

/* status badges */
.spinner {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid transparent;
  border-bottom-color: var(--primary);
  animation: spin 1s linear infinite;
}
.spinner.sm {
  width: 16px;
  height: 16px;
  border-width: 2px;
}

.status-processing {
  background: #8a6d0033;
  color: #ffd666;
}
.status-success {
  background: #0f51321a;
  color: #86efac;
}
.status-failed {
  background: #8420291a;
  color: #fca5a5;
}
.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--primary);
}

/* map */
#leaflet-map,
.map-popup {
  width: 100%;
  height: 320px;
  min-height: 200px;
  max-width: 560px;
  max-height: 350px;
  margin: 0 auto;
  box-sizing: border-box;
  z-index: 40;
}
:deep(.leaflet-container) {
  background: var(--bg);
}
:deep(.leaflet-control-attribution) {
  background: color-mix(in srgb, var(--surface) 80%, transparent);
  color: var(--primary);
}
:deep(.leaflet-control-zoom a) {
  background: var(--surface-2);
  border: 1px solid var(--primary);
  color: var(--primary);
}
:deep(.leaflet-control-zoom a:hover) {
  background: var(--primary);
  color: var(--on-primary);
}

/* borders */
.border-gold {
  border-color: var(--primary);
}
.pay-icon {
  width: 24px;
  height: 24px;
  display: inline-block;
}

/* print: keep receipt clean */
@media print {
  nav,
  .navbar,
  header,
  footer {
    display: none !important;
  }
  body * {
    visibility: hidden;
  }
  .receipt-print,
  .receipt-print * {
    visibility: visible;
  }
  .receipt-print {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    background: #ffffff !important;
    color: #000000 !important;
  }
  .receipt-print .panel,
  .receipt-print .subpanel {
    background: #ffffff !important;
    color: #000000 !important;
    border-color: #000 !important;
  }
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
